import React, { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { colors, fonts } from "../theme";

const pinIcon = L.divIcon({
  className: "bia-pin",
  html: `<div style="transform:translate(-50%,-100%)">
    <svg width="28" height="34" viewBox="0 0 28 34" fill="none">
      <path d="M14 0C6.8 0 1 5.8 1 13c0 9 13 21 13 21s13-12 13-21C27 5.8 21.2 0 14 0z" fill="#498DCC"/>
      <circle cx="14" cy="13" r="5" fill="#fff"/>
    </svg></div>`,
  iconSize: [28, 34],
  iconAnchor: [14, 34],
});

// Distinct amber marker for the .epw weather-station location
const epwStationIcon = L.divIcon({
  className: "bia-pin-epw",
  html: `<div style="transform:translate(-50%,-100%)">
    <svg width="26" height="32" viewBox="0 0 28 34" fill="none">
      <path d="M14 0C6.8 0 1 5.8 1 13c0 9 13 21 13 21s13-12 13-21C27 5.8 21.2 0 14 0z" fill="#FF9500"/>
      <rect x="9.5" y="8.5" width="9" height="9" rx="2" fill="#fff"/>
    </svg></div>`,
  iconSize: [26, 32],
  iconAnchor: [13, 32],
});

export default function LocationMap({ value, onPin, epwStation }) {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const epwMarkerRef = useRef(null);
  const onPinRef = useRef(onPin);
  const initialValueRef = useRef(value);
  useEffect(() => {
    onPinRef.current = onPin;
  }, [onPin]);

  useEffect(() => {
    if (!elRef.current || mapRef.current) return;
    const initial = initialValueRef.current;
    const map = L.map(elRef.current, {
      center: initial ? [initial.lat, initial.lng] : [56.13, -106.35], // default: Canada
      zoom: initial ? 6 : 3,
      zoomControl: false,
      attributionControl: false,
    });
    L.tileLayer(
      "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      { maxZoom: 19, subdomains: "abc", attribution: "© OpenStreetMap contributors" }
    ).addTo(map);
    L.control.zoom({ position: "bottomright" }).addTo(map);

    const place = (lat, lng) => {
      if (markerRef.current) {
        markerRef.current.setLatLng([lat, lng]);
      } else {
        const m = L.marker([lat, lng], { icon: pinIcon, draggable: true }).addTo(map);
        m.on("dragend", () => {
          const p = m.getLatLng();
          onPinRef.current?.({ lat: +p.lat.toFixed(4), lng: +p.lng.toFixed(4) });
        });
        markerRef.current = m;
      }
    };

    if (initial) place(initial.lat, initial.lng);

    map.on("click", (e) => {
      const lat = +e.latlng.lat.toFixed(4);
      const lng = +e.latlng.lng.toFixed(4);
      place(lat, lng);
      onPinRef.current?.({ lat, lng });
    });

    mapRef.current = map;
    // Ensure correct sizing after layout settles
    setTimeout(() => map.invalidateSize(), 250);

    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
  }, []);

  // Keep marker/view in sync when value is set externally (e.g. via .epw)
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !value) return;
    if (markerRef.current) markerRef.current.setLatLng([value.lat, value.lng]);
    else
      markerRef.current = L.marker([value.lat, value.lng], {
        icon: pinIcon,
        draggable: true,
      })
        .addTo(map)
        .on("dragend", (ev) => {
          const p = ev.target.getLatLng();
          onPinRef.current?.({ lat: +p.lat.toFixed(4), lng: +p.lng.toFixed(4) });
        });
    map.setView([value.lat, value.lng], Math.max(map.getZoom(), 6));
  }, [value]);

  // Secondary amber pin for the .epw weather-station coordinates
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (epwStation && epwStation.lat != null && epwStation.lng != null) {
      if (epwMarkerRef.current) {
        epwMarkerRef.current.setLatLng([epwStation.lat, epwStation.lng]);
      } else {
        epwMarkerRef.current = L.marker([epwStation.lat, epwStation.lng], {
          icon: epwStationIcon,
          zIndexOffset: -50,
        })
          .addTo(map)
          .bindTooltip(`EPW station${epwStation.name ? " · " + epwStation.name : ""}`, { direction: "top", offset: [0, -28] });
      }
    } else if (epwMarkerRef.current) {
      map.removeLayer(epwMarkerRef.current);
      epwMarkerRef.current = null;
    }
  }, [epwStation]);

  return (
    <div style={styles.wrap} data-testid="location-map">
      <div ref={elRef} style={styles.map} />
      {!value && <div style={styles.hint}>Click the map to set your project site</div>}
      {epwStation && epwStation.lat != null && (
        <div style={styles.legend} data-testid="map-epw-legend">
          <span style={styles.legendRow}><span style={{ ...styles.legendDot, background: "#498DCC" }} /> Site</span>
          <span style={styles.legendRow}><span style={{ ...styles.legendDot, background: "#FF9500" }} /> EPW station</span>
        </div>
      )}
    </div>
  );
}

const styles = {
  wrap: {
    position: "relative",
    borderRadius: 14,
    overflow: "hidden",
    border: `1px solid ${colors.border}`,
  },
  map: { width: "100%", height: 150, background: "#e8e8ec" },
  hint: {
    position: "absolute",
    left: 10,
    bottom: 10,
    padding: "6px 10px",
    borderRadius: 999,
    background: "rgba(255,255,255,0.9)",
    fontFamily: fonts.sans,
    fontSize: 11,
    color: colors.textSecondary,
    boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
    pointerEvents: "none",
    zIndex: 500,
  },
  legend: {
    position: "absolute",
    right: 8,
    top: 8,
    display: "flex",
    flexDirection: "column",
    gap: 3,
    padding: "6px 9px",
    borderRadius: 8,
    background: "rgba(255,255,255,0.92)",
    boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
    zIndex: 500,
  },
  legendRow: { display: "flex", alignItems: "center", gap: 5, fontFamily: fonts.sans, fontSize: 10, color: colors.textSecondary },
  legendDot: { width: 8, height: 8, borderRadius: 999, flexShrink: 0 },
};
